import ActionButton from "@/components/buttons/ActionButton";
import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Flex, Form, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../common/constants";
import { AgentCommonFilterFields } from "../../../../../agent/components/commonfilters/AgentCommonFilterFields";
import { AgentExtendedFilterModalForm } from "../../../../../agent/components/commonfilters/AgentExtendedFilterModalForm";
import { useIsCurrentAgentTypePlus } from "../../../../../auth/hooks";
import { CommissionsCategory } from "../../../../enums";
import { DEFAULT_FILTER_COMMISSIONS_BATCH_OUTPUTS_QUERY_PARAMS } from "../../../api";
import { CommissionsBatchOutputFilterPageRequest } from "../../../types";

type ExtendedFilter = { completedEducationDateRange?: string[]; completedAcademyDateRange?: string[] };

type Props = {
  filterData: CommissionsBatchOutputFilterPageRequest;
  onChangeFilter: (filterData: CommissionsBatchOutputFilterPageRequest) => void;
};

export const CommissionBatchOutputsFilterForm = ({ filterData, onChangeFilter }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [form] = Form.useForm<CommissionsBatchOutputFilterPageRequest & ExtendedFilter>();

  useEffect(() => {
    form.setFieldsValue({
      ...filterData,
      completedEducationDateRange:
        filterData.completedEducationDateMin && filterData.completedEducationDateMax
          ? [filterData.completedEducationDateMin, filterData.completedEducationDateMax]
          : undefined,
      completedAcademyDateRange:
        filterData.completedAcademyDateMin && filterData.completedAcademyDateMax
          ? [filterData.completedAcademyDateMin, filterData.completedAcademyDateMax]
          : undefined,
    });
  }, [filterData]);

  return (
    <Card
      title="Filter"
      size="small"
      className="margin-bottom-medium"
      extra={
        <ActionButton
          color="danger"
          size="small"
          variant="outlined"
          icon="reload"
          onClick={() => {
            form.resetFields();
            onChangeFilter(form.getFieldsValue());
          }}
        >
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={({ completedEducationDateRange, completedAcademyDateRange, ...filter }) => onChangeFilter(filter)}
        name="CommissionsBatchOutputFilterForm"
        initialValues={DEFAULT_FILTER_COMMISSIONS_BATCH_OUTPUTS_QUERY_PARAMS}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(4, 1fr)`,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <AgentCommonFilterFields />

          {isTopAgentPlus && (
            <Form.Item
              name="categories"
              label={t("commissions.enums.commissionsCategory_PLUS._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                mode="multiple"
                options={Object.values(CommissionsCategory)
                  .filter((category) => category !== CommissionsCategory.PRIMARY)
                  .map((category) => ({
                    value: category,
                    label: t(`commissions.enums.commissionsCategory_PLUS.${category}`),
                  }))}
              />
            </Form.Item>
          )}
        </div>

        <Flex justify="space-between">
          <AgentExtendedFilterModalForm
            onSubmit={(values) => {
              form.setFieldsValue(values);
              onChangeFilter(form.getFieldsValue());
            }}
          />

          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
